import { useState, useEffect } from 'react';

const InstallBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Detect device type
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;

  // Check if already installed as PWA
  const isRunningStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  useEffect(() => {
    if (isRunningStandalone) {
      setIsInstalled(true);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const isApp = urlParams.get('p') === 'app';
    const hasDismissed = sessionStorage.getItem('installBannerDismissed') === 'true';

    if (isApp || hasDismissed) return;

    // Listen for the native PWA install prompt (Chrome/Edge/Android)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show banner after short delay
    const timer = setTimeout(() => {
      // On iOS Safari: always show (no native prompt available)
      // On other browsers: show only if native prompt was captured OR as a fallback
      setIsVisible(true);
    }, 1500);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOSSafari) {
      // Show step-by-step iOS guide
      setShowIOSGuide(true);
      return;
    }

    if (deferredPrompt) {
      // Trigger native browser install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsVisible(false);
        setTimeout(() => setIsDismissed(true), 500);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: show generic guide
      setShowIOSGuide(true);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setShowIOSGuide(false);
    sessionStorage.setItem('installBannerDismissed', 'true');
    setTimeout(() => setIsDismissed(true), 500);
  };

  if (isInstalled || isDismissed) return null;

  const getInstallButtonLabel = () => {
    if (isIOSSafari) return 'How to Install';
    if (deferredPrompt) return 'Install App';
    return 'How to Install';
  };

  return (
    <>
      {/* Install Banner */}
      <div
        className={`no-print fixed bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 w-[90%] max-w-[420px] bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-white/30 dark:border-gray-600/30 shadow-[0_8px_32px_rgba(0,0,0,0.18)] rounded-2xl p-4 z-[9999] transition-all duration-500 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0'}`}
      >
        <div className="flex items-center gap-3">
          <div className="text-2xl bg-gradient-to-br from-blue-500 to-blue-700 w-12 h-12 flex items-center justify-center rounded-xl text-white shrink-0 shadow-sm">
            {isIOSSafari
              ? <i className="fa-brands fa-apple"></i>
              : isAndroid
                ? <i className="fa-brands fa-android"></i>
                : <i className="fa-solid fa-mobile-screen-button"></i>
            }
          </div>
          <div className="flex-grow flex flex-col min-w-0">
            <strong className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight">
              Better in the App
            </strong>
            <span className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {isIOSSafari
                ? 'Add to Home Screen for the best experience.'
                : deferredPrompt
                  ? 'Install in one tap for offline access.'
                  : 'Install for the full experience.'
              }
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleInstallClick}
              className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all shadow-sm whitespace-nowrap"
            >
              {getInstallButtonLabel()}
            </button>
            <button
              onClick={handleClose}
              className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-300 rounded-full w-7 h-7 flex items-center justify-center transition-colors focus:outline-none shrink-0"
            >
              <i className="fa-solid fa-xmark text-xs"></i>
            </button>
          </div>
        </div>
      </div>

      {/* iOS Step-by-Step Guide Modal */}
      {showIOSGuide && (
        <div
          className="no-print fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowIOSGuide(false); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

          {/* Modal */}
          <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden animate-fadein">
            {/* Header */}
            <div className={`px-5 pt-5 pb-4 flex items-center gap-3 border-b border-gray-100 dark:border-gray-700`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm text-lg ${isIOSSafari ? 'bg-gradient-to-br from-gray-800 to-black' : 'bg-gradient-to-br from-blue-500 to-blue-700'}`}>
                {isIOSSafari
                  ? <i className="fa-brands fa-apple"></i>
                  : isAndroid
                    ? <i className="fa-brands fa-android"></i>
                    : <i className="fa-solid fa-desktop"></i>
                }
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white text-base leading-tight">Install DL Track</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {isIOSSafari ? 'iOS Safari' : isAndroid ? 'Android' : 'Desktop / Browser'}
                </p>
              </div>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="ml-auto bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            {/* Steps */}
            <div className="px-5 py-4 space-y-3">
              {isIOSSafari ? (
                <>
                  <Step number={1} icon="fa-solid fa-share-from-square" iconColor="text-blue-500" label='Tap the Share button' description='Find the Share icon at the bottom of your Safari browser.' />
                  <Step number={2} icon="fa-solid fa-list" iconColor="text-gray-600 dark:text-gray-300" label='Scroll down in the menu' description='Swipe up in the share sheet to see more options.' />
                  <Step number={3} icon="fa-solid fa-square-plus" iconColor="text-green-500" label='Tap "Add to Home Screen"' description='Tap the icon with the plus sign labeled "Add to Home Screen".' />
                  <Step number={4} icon="fa-solid fa-check-circle" iconColor="text-blue-600" label='Tap "Add" to confirm' description='Press "Add" in the top right corner to complete installation.' />
                </>
              ) : isAndroid ? (
                <>
                  <Step number={1} icon="fa-solid fa-ellipsis-vertical" iconColor="text-gray-600 dark:text-gray-300" label='Open Chrome menu' description='Tap the three-dot menu at the top right of Chrome.' />
                  <Step number={2} icon="fa-solid fa-mobile-screen-button" iconColor="text-blue-500" label='Tap "Add to Home screen"' description='Select "Add to Home screen" or "Install app" from the menu.' />
                  <Step number={3} icon="fa-solid fa-check-circle" iconColor="text-green-500" label='Confirm installation' description='Tap "Add" or "Install" on the dialog to install the app.' />
                </>
              ) : (
                <>
                  <Step number={1} icon="fa-solid fa-download" iconColor="text-blue-500" label='Look for the install icon' description='Click the install icon (⊕) in your browser address bar.' />
                  <Step number={2} icon="fa-solid fa-check-circle" iconColor="text-green-500" label='Click "Install"' description='Confirm by clicking "Install" in the browser prompt.' />
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 pb-5">
              <button
                onClick={handleClose}
                className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Step sub-component
const Step = ({ number, icon, iconColor, label, description }) => (
  <div className="flex items-start gap-3">
    <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
      {number}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <i className={`${icon} ${iconColor} text-sm`}></i>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{label}</span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
    </div>
  </div>
);

export default InstallBanner;
